/**
 * savedLabels — the ONE loader for "this swim, with the owner's saved stroke
 * corrections applied" (lane stroke-sync, 2026-09-12).
 *
 * Before this module five surfaces derived a swim with `deriveWorkout(fh, [])`
 * (the Sets ledger ribbons and history scan, Share for a set not on screen, the
 * Club shared morning and set compare), so a correction showed on the Swim tab
 * and nowhere else. Every surface that derives a swim other than through the
 * live Swim panel reads its labels here.
 *
 * Trust rule (same as resolveLabelsOnLoad): the swim already open in THIS heap
 * reads the in-memory labels, which include a commit still in flight to disk;
 * any other swim reads native. A failed native read derives the watch's strokes
 * and says so in the console; it never throws into a render.
 */
import { get } from "svelte/store";
import type { LapLabel, SwimFirehoseResponse } from "@contract/types";
import { bridge } from "../bridge/client";
import { currentLabels, currentRawFirehose } from "./state";
import { deriveWorkout, type DerivedWorkout } from "./lapAnalysis";

export type ReadLabels = (workoutUUID: string) => Promise<LapLabel[]>;

const readNative: ReadLabels = (uuid) => bridge.getLapLabels(uuid);

export async function loadSavedLabels(workoutUUID: string, read: ReadLabels = readNative): Promise<LapLabel[]> {
  const open = get(currentRawFirehose);
  if (open && open.workout.uuid === workoutUUID) return get(currentLabels);
  try {
    const labels = await read(workoutUUID);
    return Array.isArray(labels) ? labels : [];
  } catch (err) {
    console.warn(`[labels] could not read saved corrections for ${workoutUUID}; showing the watch's strokes`, err);
    return [];
  }
}

export async function deriveWithSavedLabels(
  fh: SwimFirehoseResponse,
  read: ReadLabels = readNative,
): Promise<DerivedWorkout> {
  return deriveWorkout(fh, await loadSavedLabels(fh.workout.uuid, read));
}
